import {
  Users,
  Image as ImageIcon,
  CreditCard,
  User,
  Film,
  Coins,
  TrendingUp,
  ShoppingBag,
  Percent,
  Gift,
  AlertTriangle,
} from 'lucide-react';
import type { Stats } from './types';

const FAILURE_CATEGORY_LABELS: Record<string, string> = {
  moderation: '审核',
  infrastructure: '基建',
  generation: '生成',
  param: '参数',
  config: '配置',
  unknown: '其他',
};

const FAILURE_CATEGORY_ORDER = ['moderation', 'param', 'config', 'infrastructure', 'generation', 'unknown'];

function failureBreakdownText(breakdown: Record<string, number>): string | undefined {
  const entries = FAILURE_CATEGORY_ORDER
    .filter((key) => breakdown[key] > 0)
    .map((key) => `${FAILURE_CATEGORY_LABELS[key] ?? FAILURE_CATEGORY_LABELS.unknown} ${breakdown[key]}`);
  return entries.length > 0 ? entries.join(' · ') : undefined;
}

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

function StatCard({ title, value, subtitle, icon: Icon, color }: StatCardProps) {
  return (
    <div className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-light-border">
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-light-muted text-xs lg:text-sm">{title}</p>
          <p className="text-2xl lg:text-3xl font-bold text-light-text mt-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtitle && <p className="text-xs text-light-muted mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${color} flex items-center justify-center shrink-0 ml-3`}>
          <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return <h3 className="text-base font-semibold text-light-text mb-3 mt-2">{title}</h3>;
}

interface DashboardTabProps {
  stats: Stats | null;
}

export default function DashboardTab({ stats }: DashboardTabProps) {
  if (!stats) return null;

  const formatRMB = (cents: number) => `¥${(cents).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6">
      {/* 用户 */}
      <div>
        <SectionTitle title="用户" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="总用户数" value={stats.totalUsers} icon={Users} color="bg-blue-500" />
          <StatCard title="今日新增" value={stats.todayUsers} icon={User} color="bg-orange-500" />
          <StatCard title="当日登录用户" value={stats.todayLoginUsers} icon={User} color="bg-sky-500" />
          <StatCard title="今日签到人数" value={stats.todayCheckInUsers} icon={Users} color="bg-cyan-500" />
        </div>
      </div>

      {/* 支付 */}
      <div>
        <SectionTitle title="支付订单" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="总订单数" value={stats.totalOrders} icon={ShoppingBag} color="bg-emerald-500" />
          <StatCard title="今日订单" value={stats.todayOrders} icon={ShoppingBag} color="bg-teal-500" />
          <StatCard
            title="累计收入"
            value={formatRMB(stats.totalRevenue)}
            icon={TrendingUp}
            color="bg-amber-500"
          />
          <StatCard
            title="今日收入"
            value={formatRMB(stats.todayRevenue)}
            icon={CreditCard}
            color="bg-yellow-500"
          />
        </div>
      </div>

      {/* 积分 */}
      <div>
        <SectionTitle title="积分状态" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="已发放积分" value={stats.totalCreditsIssued} icon={Coins} color="bg-amber-500" />
          <StatCard title="非充值积分" value={stats.nonRechargeCredits} icon={Gift} color="bg-emerald-500" />
          <StatCard title="已消耗积分" value={stats.totalCreditsConsumed} icon={TrendingUp} color="bg-violet-500" />
          <StatCard
            title="积分消耗率"
            value={`${stats.creditConsumptionRate.toFixed(2)}%`}
            icon={Percent}
            color="bg-cyan-500"
          />
        </div>
      </div>

      {/* 图片 */}
      <div>
        <SectionTitle title="图片生成" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="总图片数" value={stats.totalImages} icon={ImageIcon} color="bg-green-500" />
          <StatCard title="今日生成" value={stats.todayImages} icon={ImageIcon} color="bg-pink-500" />
          <StatCard
            title="今日失败"
            value={stats.todayImageFailures}
            subtitle={failureBreakdownText(stats.todayImageFailureBreakdown)}
            icon={AlertTriangle}
            color="bg-red-500"
          />
          <StatCard title="积分消耗" value={stats.totalCreditsSpent} icon={Coins} color="bg-purple-500" />
        </div>
      </div>

      {/* 视频 */}
      <div>
        <SectionTitle title="视频生成" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="总视频数" value={stats.totalVideos} icon={Film} color="bg-indigo-500" />
          <StatCard title="今日生成" value={stats.todayVideos} icon={Film} color="bg-rose-500" />
          <StatCard title="今日失败" value={stats.todayVideoFailures} icon={AlertTriangle} color="bg-red-500" />
          <StatCard title="积分消耗" value={stats.videoCreditsSpent} icon={Coins} color="bg-fuchsia-500" />
        </div>
      </div>

    </div>
  );
}
